"use client";

import React from "react";
import { DollarSign, Check, X, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTimeEntryHooks } from "@/features/time-entries/hooks/time-entry-hooks";
import { toast } from "sonner";

import type { EditableBillableRateProps } from "../types";

/**
 * Inline-editable Billable Rate. Empty → "Add rate" button; populated → the
 * value with a hover pencil. Persists via useTimeEntryHooks().useUpdate() —
 * billable_rate is a BigFloat column, but the hook serializes it, so pass a
 * plain number (or null to clear). Local mirror re-seeds on entryId.
 */
export function EditableBillableRate({
  entryId,
  rate,
  onRefresh,
}: EditableBillableRateProps) {
  const { mutateAsync, isPending } = useTimeEntryHooks().useUpdate();

  const initial = typeof rate === "number" ? rate : null;
  const [value, setValue] = React.useState<number | null>(initial);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(initial != null ? String(initial) : "");

  React.useEffect(() => {
    setValue(initial);
    if (!isEditing) setDraft(initial != null ? String(initial) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, entryId]);

  const startEdit = () => {
    setDraft(value != null ? String(value) : "");
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(value != null ? String(value) : "");
    setIsEditing(false);
  };

  const save = async () => {
    const trimmed = draft.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (next != null && (!Number.isFinite(next) || next < 0)) {
      toast.error("Enter a valid rate");
      return;
    }
    if (next === value) {
      setIsEditing(false);
      return;
    }
    try {
      await mutateAsync({ id: entryId, billable_rate: next });
      setValue(next);
      setIsEditing(false);
      toast.success("Billable rate updated");
      onRefresh?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update billable rate");
    }
  };

  if (isEditing) {
    return (
      <div className="mt-1">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="0.00"
            autoFocus
            disabled={isPending}
            className="h-8 w-32 text-sm"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              } else if (e.key === "Enter") {
                e.preventDefault();
                void save();
              }
            }}
          />
          <Button type="button" size="sm" onClick={() => void save()} disabled={isPending}>
            <Check className="h-4 w-4 mr-1" />
            {isPending ? "Saving…" : "Save"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={isPending}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (value != null) {
    return (
      <div className="group/rate flex items-center mt-1">
        <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
        <span className="text-sm text-card-foreground">{`$${value}/h`}</span>
        <button
          type="button"
          onClick={startEdit}
          className="opacity-0 group-hover/rate:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 ml-2"
          title="Edit billable rate"
          aria-label="Edit billable rate"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <Button type="button" size="sm" variant="outline" onClick={startEdit} disabled={isPending}>
        <Plus className="h-4 w-4 mr-1" />
        Add rate
      </Button>
    </div>
  );
}
